import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { calculateNewAbundances } from 'constants/fcm';
import { useDispatch, useSelector } from 'react-redux';
import { setField } from 'actions/nodeSettings';
import { pseudoTrophicLayerName, trophicLevels } from 'constants/nodes';
import {
  SettingsImage,
  SettingsNumberField,
  SettingsDescription,
  SettingsButtonSet,
  SettingsDropdown,
} from 'components/SettingsNew';
import AbundanceSlider from 'components/inputs/AbundanceSlider';
import {
  augmentedMembership,
  membershipCategoryFilter,
  rescale,
  descale,
} from 'fcm/fcm';
import { getAbundanceForValue, getValueForAbundance } from 'constants/fcm';
import './NodeSettings.scss';
import NodePill from '../../NodePill';
import Icon from '../../Icon';
// Move this into node settings
import NodeSettingsConnections from 'components/NodeSettingsConnections';
import { useViewState } from '../../../state/ViewState';

const makeOption = value => ({ label: value, value });

const InputOutputStatic = ({ fixed }) => {
  const text = useMemo(() => (fixed ? 'Input' : 'Output'), [fixed]);
  return (
    <div className="InputOutputStatic">
      <Icon icon={fixed ? 'inputicon' : 'outputicon'} />
      {text}
    </div>
  );
};

// Basic info settings
const NodeBasicSettings = ({ matrix, node, readOnly, isEditing, onSave }) => {
  const dispatch = useDispatch();

  const { setShowConnectionsTable, setConnectionsTabOpen } = useViewState(
    state => state
  );

  const { validationMessages } = useSelector(state => state.matrixSettings);
  const { user } = useSelector(state => state.auth);
  // TODO see if this is valid
  const modalRef = document.getElementById('matrix-modal');

  const matrixTrophicLevels = useMemo(() => {
    return matrix?.trophicLevels ?? trophicLevels;
  }, [matrix]);

  const availableTrophicLevels = Array.from(
    new Set(
      [
        ...matrixTrophicLevels.map(({ name }) => name),
        ...Object.values(matrix.nodes).map(node => node.trophicLevel),
      ].filter(t => t !== pseudoTrophicLayerName)
    )
  );

  // Population/abundance
  const membershipCategories = membershipCategoryFilter(
    augmentedMembership,
    node?.membershipFunctions
  );

  const [minValue, setMin] = useState(
    node?.membershipFunctions[membershipCategories[0]][0][0]
  );
  const [meanValue, setMean] = useState(node?.mean);
  const [maxValue, setMax] = useState(
    node?.membershipFunctions[
      membershipCategories[membershipCategories.length - 1]
    ][2][0]
  );

  const conditionalUpdate = () => {
    if (isNaN(minValue) || isNaN(meanValue) || isNaN(maxValue)) return;
    if (minValue >= meanValue || minValue >= maxValue) return;
    if (maxValue <= meanValue || maxValue <= minValue) return;

    let scaledFunctions = rescale(
      JSON.parse(JSON.stringify(node?.membershipFunctions)),
      node?.mean
    );
    let newFunctions = descale(scaledFunctions, meanValue, minValue, maxValue);

    dispatch(setField('membershipFunctions', newFunctions));
    dispatch(setField('mean', meanValue));
  };

  useEffect(conditionalUpdate, [minValue, meanValue, maxValue]);

  const onGoToConnectionsTable = useCallback(() => {
    onSave();
    setConnectionsTabOpen(true);
    setShowConnectionsTable(true);
  }, [onSave, setShowConnectionsTable, setConnectionsTabOpen]);

  return (
    <div className="NodeSettingsBasic">
      <div className="NodeSettings-section-title">Basic information</div>

      <div className="NodeSettings-row">
        <div className="NodeSettings-half">
          <SettingsImage
            disabled={readOnly || !user}
            onChange={value => {
              dispatch(setField('imageUrl', value));
              dispatch(setField('displayImageUrl', value));
            }}
            value={node.displayImageUrl}
            description="Recommended photo size is no less than 450 x 300px"
            isStatic={!isEditing}
          />
        </div>

        <div className="NodeSettings-column NodeSettings-half">
          {/* Description */}
          <SettingsDescription
            label="Description"
            disabled={readOnly}
            onChange={value => dispatch(setField('description', value))}
            placeholder={'Add a short description of your node.'}
            value={node.description}
            validationMessage={validationMessages.description}
            isStatic={!isEditing}
          />

          {/* Trophic level */}
          <SettingsDropdown
            creatable
            disabled={readOnly}
            label="Trophic Level"
            onChange={value => dispatch(setField('trophicLevel', value))}
            options={availableTrophicLevels.map(makeOption)}
            placeholder="Select Trophic Level"
            validationMessage={validationMessages.trophicLevel}
            value={node.trophicLevel}
            menuPortalTarget={document.body}
            closeMenuOnScroll={event =>
              modalRef && modalRef.firstChild.contains(event.target)
            }
            description={
              'Based on the position the node occupies in a food web'
            }
            isStatic={!isEditing}
            staticPlaceholder={<NodePill node={node} width={'100%'} />}
          />

          {/* Set input/output */}
          <SettingsButtonSet
            disabled={readOnly}
            label="Node Type"
            onChange={value => {
              const isSame = node?.fixed
                ? value === 'Input'
                : value === 'Output';

              if (isSame) return;

              dispatch(setField('fixed', value === 'Input'));

              if (value === 'Input') {
                let modifiedMatrix = {
                  ...matrix,
                  nodes: {
                    ...matrix.nodes,
                    [node.id]: { ...matrix.nodes[node.id], fixed: false },
                  },
                };

                const matrixOldAbundance = Object.fromEntries(
                  Object.values(matrix.nodes).map(({ id, abundance }) => [
                    id,
                    abundance,
                  ])
                );

                const matrixNewAbundances = calculateNewAbundances(
                  modifiedMatrix,
                  matrixOldAbundance
                );

                dispatch(setField('abundance', matrixNewAbundances[node.id]));
              }
            }}
            options={[
              { value: 'Input', label: 'Input', icon: 'inputicon' },
              { value: 'Output', label: 'Output', icon: 'outputicon' },
            ]}
            value={node.fixed ? 'Input' : 'Output'}
            description={
              'Input abundances stay fixed and will not react to other nodes when you run the model'
            }
            isStatic={!isEditing}
            staticPlaceholder={<InputOutputStatic fixed={node?.fixed} />}
          />
        </div>
      </div>
      <div className="NodeSettings-row">
        <div className="NodeSettings-column">
          <div className="NodeSettings-section-title">Population</div>
          <div className="NodeSettings-section-text">
            Set the starting abundance for this node in the matrix. The matrix
            will run to equilibrium by default, so if this node is set as an
            output, this abundance may change slightly as it reacts to the other
            nodes it’s connected to.
          </div>
        </div>
        <div className="NodeSettings-column">
          {/* Abundance */}
          <SettingsNumberField
            disabled={readOnly}
            label="Abundance"
            onChange={value =>
              dispatch(setField('abundance', getAbundanceForValue(node, value)))
            }
            value={getValueForAbundance(node, node?.abundance)}
            min={minValue}
            max={maxValue}
            isStatic={!isEditing}
          />
          <AbundanceSlider
            changeable
            node={node}
            onChange={value => dispatch(setField('abundance', value))}
            value={node.abundance}
            isStatic={!isEditing}
          />
        </div>
      </div>

      <div className="NodeSettings-row grow">
        <div className="NodeSettings-column">
          <div className="NodeSettings-section-title">Connections</div>

          <div className="NodeSettings-section-text NodeSettings-half">
            The following are the connections of this node. To see and edit
            connections for all nodes, go to the{' '}
            <button
              className="NodeSettings-connections-table-button"
              onClick={onGoToConnectionsTable}
            >
              connections table
            </button>
            .
          </div>

          <NodeSettingsConnections
            matrix={matrix}
            nodeId={node?.id}
            isStatic={!isEditing}
          />
        </div>
      </div>
    </div>
  );
};

export default NodeBasicSettings;
